// Per-browser wizard preferences. Deliberately cookie-backed with no server
// state: clearing browser data or the max-age lapsing brings the default back.

import {
    CONTENT_AUDIENCES,
    ContentAudience,
} from "../types/contentAudience";

const DAY_SECONDS = 24 * 60 * 60;

interface PrefCookie {
    isHidden: () => boolean;
    setHidden: (hidden: boolean) => void;
}

export interface PrefValueCookie<T extends string> {
    get: () => T;
    set: (value: T) => void;
}

export function prefCookie(name: string, maxAgeSeconds: number): PrefCookie {
    return {
        isHidden: () =>
            document.cookie
                .split(";")
                .some((c) => c.trim().startsWith(`${name}=1`)),
        setHidden: (hidden) => {
            document.cookie = hidden
                ? `${name}=1; max-age=${maxAgeSeconds}; path=/; SameSite=Lax`
                : `${name}=; max-age=0; path=/; SameSite=Lax`;
        },
    };
}

export const welcomeOverlayPref = prefCookie(
    "marabot_hide_welcome",
    90 * DAY_SECONDS
);

export const connectionGuidePref = prefCookie(
    "marabot_hide_connection_guide",
    365 * DAY_SECONDS
);

// Same storage as prefCookie, for a preference that remembers a value rather
// than a yes/no. A missing, stale, or hand-edited cookie falls back to the
// default, so the wizard can never open on a value it does not support.
export function prefValueCookie<T extends string>(
    name: string,
    maxAgeSeconds: number,
    allowed: readonly T[],
    fallback: T
): PrefValueCookie<T> {
    return {
        get: () => {
            const entry = document.cookie
                .split(";")
                .map((c) => c.trim())
                .find((c) => c.startsWith(`${name}=`));
            if (!entry) return fallback;

            const value = decodeURIComponent(entry.slice(name.length + 1));
            return (allowed as readonly string[]).includes(value)
                ? (value as T)
                : fallback;
        },
        set: (value) => {
            document.cookie = `${name}=${encodeURIComponent(
                value
            )}; max-age=${maxAgeSeconds}; path=/; SameSite=Lax`;
        },
    };
}

// The Lead Generation and MyBrand wizards open on whichever voice this user
// picked last. The choice stays per piece — this only seeds the default.
//
// Keyed by the effective user: an admin drafting a commercial piece while
// impersonating must not seed the next account's wizard, and a shared browser
// must not carry one person's choice into the next login. With no user there
// is nothing to remember, so it reads as the default and stores nothing.
export function contentAudiencePref(
    userId: string | null | undefined
): PrefValueCookie<ContentAudience> {
    if (!userId) {
        return {
            get: () => ContentAudience.Residential,
            set: () => undefined,
        };
    }

    return prefValueCookie<ContentAudience>(
        `marabot_content_audience_${userId}`,
        365 * DAY_SECONDS,
        CONTENT_AUDIENCES,
        ContentAudience.Residential
    );
}

// The recorder's caption band and face guide, on unless this user turned them
// off. Keyed by the effective user like the prefs above; with no user it reads
// on and stores nothing.
export type RecorderGuides = "on" | "off";
const RECORDER_GUIDES: readonly RecorderGuides[] = ["on", "off"];

export function recorderGuidesPref(
    userId: string | null | undefined
): PrefValueCookie<RecorderGuides> {
    if (!userId) {
        return {
            get: () => "on",
            set: () => undefined,
        };
    }

    return prefValueCookie<RecorderGuides>(
        `marabot_recorder_guides_${userId}`,
        365 * DAY_SECONDS,
        RECORDER_GUIDES,
        "on"
    );
}
